import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Fontaine — shared floating positioning (Select, Combobox, Menu, Popover, Tooltip, DatePicker).
// Fixed-position coordinates for `floating` relative to `anchor`, flipping to the other side when the
// preferred side lacks room and clamping horizontally inside the viewport. No dependency.

const GAP = 6;
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * @param {React.RefObject<HTMLElement>} anchorRef
 * @param {React.RefObject<HTMLElement>} floatingRef
 * @param {{ open: boolean, side?: "bottom"|"top"|"right"|"left", align?: "start"|"center"|"end", matchWidth?: boolean, offset?: number }} opts
 * @returns {{ style: React.CSSProperties, side: string, update: () => void }}
 */
export default function usePosition(anchorRef, floatingRef, { open, side = "bottom", align = "start", matchWidth = false, offset = GAP } = {}) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: undefined, side, ready: false });

  const update = useCallback(() => {
    const a = anchorRef.current;
    const f = floatingRef.current;
    if (!a || !f) return;
    const ar = a.getBoundingClientRect();
    const fr = f.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let s = side;
    const fh = fr.height || 0;
    const fw = matchWidth ? ar.width : fr.width || 0;

    // vertical flip
    if (s === "bottom" && ar.bottom + offset + fh > vh && ar.top - offset - fh >= 0) s = "top";
    else if (s === "top" && ar.top - offset - fh < 0 && ar.bottom + offset + fh <= vh) s = "bottom";
    // horizontal flip
    if (s === "right" && ar.right + offset + fw > vw && ar.left - offset - fw >= 0) s = "left";
    else if (s === "left" && ar.left - offset - fw < 0 && ar.right + offset + fw <= vw) s = "right";

    let top;
    let left;
    if (s === "bottom" || s === "top") {
      top = s === "bottom" ? ar.bottom + offset : ar.top - offset - fh;
      left = align === "end" ? ar.right - fw : align === "center" ? ar.left + ar.width / 2 - fw / 2 : ar.left;
      left = Math.max(GAP, Math.min(left, vw - fw - GAP));
    } else {
      left = s === "right" ? ar.right + offset : ar.left - offset - fw;
      top = align === "end" ? ar.bottom - fh : align === "center" ? ar.top + ar.height / 2 - fh / 2 : ar.top;
      top = Math.max(GAP, Math.min(top, vh - fh - GAP));
    }
    setPos({ top: Math.round(top), left: Math.round(left), width: matchWidth ? Math.round(ar.width) : undefined, side: s, ready: true });
  }, [anchorRef, floatingRef, side, align, matchWidth, offset]);

  useIsoLayoutEffect(() => {
    if (!open) { setPos((p) => ({ ...p, ready: false })); return undefined; }
    update();
    // second pass once the floating element has its real size
    const raf = requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [open, update]);

  return {
    side: pos.side,
    update,
    style: { position: "fixed", top: pos.top, left: pos.left, width: pos.width, visibility: pos.ready ? "visible" : "hidden" },
  };
}
